// Versiyon: 1.0.3 - Stok Sayfası Tasarımı
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Menu,
  MenuItem,
  ButtonGroup,
  CircularProgress,
  Alert,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PaletteIcon from '@mui/icons-material/Palette';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import OfferDisplayModal from './OfferDisplayModal';
import type { GivenOfferData, ReceivedOfferData } from './OfferDisplayModal';
import InvoiceDisplayModal from '../invoice/InvoiceDisplayModal';
import type { InvoiceData } from '../invoice/InvoiceDisplayModal';
import OrderDisplayModal from '../orders/OrderDisplayModal';
import type { OrderData } from '../orders/OrderDisplayModal';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import ProjectBadge from '../shared/ProjectBadge';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useTranslation } from 'react-i18next';
import {
  OFFER_FILTERS_EVENT,
  consumePendingOfferFilters,
  type OfferFilterRequest,
} from '../../aichat/agent/uiBridge';

// Teklif arayüzleri
interface GivenOffer {
  id: string;
  offerNumber: string;
  accountId: string;
  account: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  validUntil: string;
  subtotal: number;
  vatTotal: number;
  sctTotal: number;
  discountTotal: number;
  additionalDiscountAmount?: number;
  discountType?: 'percentage' | 'amount' | null;
  discountValue?: number | null;
  grandTotal: number;
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'invoiced';
  currency: string;
  createdAt: string;
  updatedAt?: string;
  notes?: string;
  linkedInvoiceId?: string; // ✅ YENİ: Oluşturulan faturanın ID'si
  linkedInvoiceNumber?: string; // ✅ YENİ: Oluşturulan faturanın numarası
  items?: Array<{
    id: string;
    name?: string;
    itemName?: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    vatRate: number;
    sctRate?: number;
    totalPrice: number;
    grossTotal?: number;
  }>;
}

interface ReceivedOffer {
  id: string;
  offerNumber: string;
  supplierId: string;
  supplier: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  validUntil: string;
  amount: number;
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'invoiced';
  currency: string;
  createdAt: string;
  updatedAt?: string;
  linkedInvoiceId?: string; // ✅ YENİ: Oluşturulan faturanın ID'si
  linkedInvoiceNumber?: string; // ✅ YENİ: Oluşturulan faturanın numarası
  items?: Array<{
    id: string;
    name: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    vatRate: number;
    sctRate?: number;
    totalPrice: number;
    grossTotal?: number;
  }>;
  notes?: string;
}

interface UnifiedOffer {
  id: string;
  offerNumber: string;
  type: 'given' | 'received';
  companyName: string;
  /** PYS: bağlı olduğu projenin adı (bağ yoksa null). */
  projectName?: string | null;
  date: string;
  validUntil: string;
  amount: number;
  status: string;
  currency: string;
  createdAt: string;
  notes?: string;
  linkedInvoiceId?: string;
  linkedInvoiceNumber?: string;
  linkedOrderId?: string;
  linkedOrderNumber?: string;
  originalOffer: GivenOffer | ReceivedOffer;
}

const Offers: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  // Teklif türü filtresi için state (tab yerine)
  const [offerTypeFilter, setOfferTypeFilter] = useState<'all' | 'given' | 'received'>('all');
  
  // Filtreleri göster/gizle
  const [showFilters, setShowFilters] = useState(false);
  
  // 3 nokta menüsü için state
  const [anchorElFeatures, setAnchorElFeatures] = useState<null | HTMLElement>(null);
  
  // Sıralama durumu için state
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  
  // Arama terimi için state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Durum filtresi için state
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Açılır menü için state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [selectedOfferType, setSelectedOfferType] = useState<'given' | 'received'>('given');

  // Silme onay modalı için state (silinecek teklifin bilgisi)
  const [deleteConfirmOffer, setDeleteConfirmOffer] = useState<{ id: string; offerNumber: string; type: 'given' | 'received' } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Veri durumu için state
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<UnifiedOffer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Modal için state'ler
  const [isDisplayModalOpen, setIsDisplayModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<GivenOfferData | ReceivedOfferData | null>(null);
  const [selectedOfferModalType, setSelectedOfferModalType] = useState<'given' | 'received'>('given');
  
  // Fatura görüntüleme modal state'leri
  const [isInvoiceDisplayModalOpen, setIsInvoiceDisplayModalOpen] = useState(false);
  const [selectedInvoiceForDisplay, setSelectedInvoiceForDisplay] = useState<InvoiceData | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  
  // Sipariş görüntüleme modal state'leri
  const [isOrderDisplayModalOpen, setIsOrderDisplayModalOpen] = useState(false);
  const [selectedOrderForDisplay, setSelectedOrderForDisplay] = useState<OrderData | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  
  // Excel dışa aktarma için loading state'i
  const [loadingExport, setLoadingExport] = useState(false);
  
  // 3 nokta menüsü handler'ları
  const handleOpenFeaturesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFeatures(event.currentTarget);
  };
  
  const handleCloseFeaturesMenu = () => {
    setAnchorElFeatures(null);
  };
  
  // Sayfa değişimi handler
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };
  
  // Sayfa başına satır sayısı değişimi handler
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Sıralama değişikliği için handler
  const handleSortChange = (column?: string) => {
    if (column && column !== sortColumn) {
      setSortColumn(column);
      setSortDirection('desc');
    } else {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };
  
  // Menü açma handler'ı
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string, type: 'given' | 'received' = 'given') => {
    setAnchorEl(event.currentTarget);
    setSelectedOfferId(id);
    setSelectedOfferType(type);
  };
  
  // Menü kapatma handler'ı
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOfferId(null);
  };

  // PDF indirme işlemi
  // Liste kaydında teklif kalemleri (items) bulunmadığı için görüntüleme ile aynı
  // detay çekme akışı kullanılır; aksi halde modal "Satır bulunmuyor" gösterir
  const handleDownloadPdf = () => {
    if (!selectedOfferId || !companyId) {
      handleMenuClose();
      return;
    }

    const currentOfferId = selectedOfferId;
    const unifiedOffer = offers.find(o => o.id === currentOfferId);
    handleMenuClose();

    if (!unifiedOffer) return;
    if (unifiedOffer.type === 'given') {
      handleViewOffer(currentOfferId);
    } else {
      handleViewReceivedOffer(currentOfferId);
    }
  };
  
  // Teklifi faturaya dönüştürme işlemi - Önce API'den detaylı teklif çeker, sonra fatura sayfasına aktarır
  const handleConvertToInvoice = async () => {
    if (!selectedOfferId || !companyId) {
      handleMenuClose();
      return;
    }
    
    // Menüyü önce kapat - MUI Popover anchorEl hatasını önler
    const currentOfferId = selectedOfferId;
    const currentOfferType = selectedOfferType;
    handleMenuClose();
    
    try {
      setLoading(true);
      
      // API'den detaylı teklif bilgisini çek (items dahil)
      console.log('📤 [API] Teklif detayları çekiliyor:', currentOfferId);
      const response = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(currentOfferId));
      
      const result = response.data;
      console.log('📥 [API] Teklif detayı yanıtı:', result);
      
      if (!result.success || !result.data) {
        throw new Error(result.message || 'Teklif detayları yüklenemedi');
      }
      
      const offerData = result.data;
      
      if (currentOfferType === 'given') {
        console.log('📤 Verilen teklif verileri satış faturasına aktarılıyor:', offerData);
        
        // Teklif verilerini localStorage'a kaydet (satış faturası için)
        const offerDataForInvoice = {
          offerId: offerData.id,
          offerNumber: offerData.offerNumber,
          accountId: offerData.accountId?.toString() || '',
          account: {
            companyName: offerData.accountName || '',
            taxNumber: offerData.accountTaxNumber || '',
            taxOffice: offerData.accountTaxOffice || '',
            address: offerData.accountAddress || '',
            phone: offerData.accountPhone || '',
            email: offerData.accountEmail || '',
          },
          items: offerData.items?.map((item: any) => ({
            id: item.id,
            itemId: item.itemId || item.id, // ✅ Stok ID'si (backend'de ItemId olarak geliyor)
            stockId: item.itemId || item.id, // ✅ Alternatif isim
            name: item.itemName,
            itemName: item.itemName,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            priceType: item.priceType || 'VAT_EXCLUDED',
            enteredPrice: item.enteredPrice || item.unitPrice,
            vatRate: item.vatRate,
            sctRate: item.sctRate || 0,
            discountRate: item.discountRate || 0,
            totalPrice: item.grossTotal || 0,
            netTotal: item.netTotal || 0,
            vatTotal: item.vatTotal || 0,
            vatAmount: item.vatAmount || item.vatTotal || 0, // ✅ Her iki ismi de destekle
            grossTotal: item.grossTotal || 0,
            warehouseId: item.warehouseId || '', // ✅ Depo ID'si
          })) || [],
          notes: offerData.notes ? `${tt('Teklif No', 'Offer No')}: ${offerData.offerNumber} - ${offerData.notes}` : `${tt('Teklif No', 'Offer No')}: ${offerData.offerNumber}`,
          currency: offerData.currency || 'TRY',
          subtotal: offerData.subtotal || 0,
          vatTotal: offerData.vatTotal || 0,
          sctTotal: offerData.sctTotal || 0,
          discountTotal: offerData.discountTotal || 0,
          grandTotal: offerData.grandTotal || 0,
        };
        
        // Items'lara vatAmount ve unitPriceWithVat ekle
        offerDataForInvoice.items = offerDataForInvoice.items.map((item: any) => ({
          ...item,
          vatAmount: item.vatTotal || 0,
          unitPriceWithVat: item.priceType === 'VAT_INCLUDED' 
            ? item.enteredPrice 
            : Math.round((item.unitPrice * (1 + (item.vatRate || 0) / 100)) * 100) / 100
        }));
        
        console.log('💾 localStorage\'a kaydedilen veri:', offerDataForInvoice);
        localStorage.setItem('offerToInvoice', JSON.stringify(offerDataForInvoice));
        
        // Yeni satış faturası sayfasına yönlendir
        navigate('/fatura/satis/yeni?fromOffer=true');
      } else {
        console.log('📤 Alınan teklif verileri alış faturasına aktarılıyor:', offerData);
        
        // Teklif verilerini localStorage'a kaydet (alış faturası için)
        const offerDataForInvoice = {
          offerId: offerData.id,
          offerNumber: offerData.offerNumber,
          supplierId: offerData.accountId?.toString() || '',
          supplier: {
            companyName: offerData.accountName || '',
            taxNumber: offerData.accountTaxNumber || '',
            taxOffice: offerData.accountTaxOffice || '',
            address: offerData.accountAddress || '',
            phone: offerData.accountPhone || '',
            email: offerData.accountEmail || '',
          },
          items: offerData.items?.map((item: any) => ({
            id: item.id,
            itemId: item.itemId || item.id, // ✅ Stok ID'si
            stockId: item.itemId || item.id, // ✅ Alternatif isim
            name: item.itemName,
            itemName: item.itemName,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            priceType: item.priceType || 'VAT_EXCLUDED',
            enteredPrice: item.enteredPrice || item.unitPrice,
            vatRate: item.vatRate,
            sctRate: item.sctRate || 0,
            discountRate: item.discountRate || 0,
            totalPrice: item.grossTotal || 0,
            netTotal: item.netTotal || 0,
            vatTotal: item.vatTotal || 0,
            vatAmount: item.vatAmount || item.vatTotal || 0, // ✅ Her iki ismi de destekle
            grossTotal: item.grossTotal || 0,
            warehouseId: item.warehouseId || '', // ✅ Depo ID'si
          })) || [],
          notes: offerData.notes ? `${tt('Teklif No', 'Offer No')}: ${offerData.offerNumber} - ${offerData.notes}` : `${tt('Teklif No', 'Offer No')}: ${offerData.offerNumber}`,
          currency: offerData.currency || 'TRY',
          amount: offerData.grandTotal || 0,
        };
        
        // Items'lara vatAmount ve unitPriceWithVat ekle
        offerDataForInvoice.items = offerDataForInvoice.items.map((item: any) => ({
          ...item,
          vatAmount: item.vatTotal || 0,
          unitPriceWithVat: item.priceType === 'VAT_INCLUDED' 
            ? item.enteredPrice 
            : Math.round((item.unitPrice * (1 + (item.vatRate || 0) / 100)) * 100) / 100
        }));
        
        console.log('💾 localStorage\'a kaydedilen veri:', offerDataForInvoice);
        localStorage.setItem('offerToInvoice', JSON.stringify(offerDataForInvoice));
        
        // Yeni alış faturası sayfasına yönlendir
        navigate('/fatura/alis/yeni?fromOffer=true');
      }
    } catch (err: any) {
      console.error('❌ Teklif detayları yüklenirken hata:', err);
      setError(err.message || 'Teklif detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };
  
  // ✅ Teklifi siparişe dönüştürme işlemi - localStorage + navigate pattern
  const handleConvertToOrder = async () => {
    if (!selectedOfferId || !companyId) {
      handleMenuClose();
      return;
    }
    
    const currentOfferId = selectedOfferId;
    const currentOfferType = selectedOfferType;
    handleMenuClose();
    
    try {
      setLoading(true);
      
      console.log('📤 [API] Teklif detayları çekiliyor (sipariş için):', currentOfferId);
      const response = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(currentOfferId));
      
      const result = response.data;
      console.log('📥 [API] Teklif detayı yanıtı:', result);
      
      if (!result.success || !result.data) {
        throw new Error(result.message || 'Teklif detayları yüklenemedi');
      }
      
      const offerData = result.data;
      
      const offerDataForOrder = {
        offerId: offerData.id,
        offerNumber: offerData.offerNumber,
        accountId: offerData.accountId?.toString() || '',
        account: {
          companyName: offerData.accountName || '',
          taxNumber: offerData.accountTaxNumber || '',
          taxOffice: offerData.accountTaxOffice || '',
          address: offerData.accountAddress || '',
          phone: offerData.accountPhone || '',
          email: offerData.accountEmail || '',
        },
        items: offerData.items?.map((item: any) => ({
          id: item.id,
          itemId: item.itemId || item.id,
          stockId: item.itemId || item.id,
          name: item.itemName,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          priceType: item.priceType || 'VAT_EXCLUDED',
          enteredPrice: item.enteredPrice || item.unitPrice,
          vatRate: item.vatRate,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          totalPrice: item.grossTotal || 0,
          netTotal: item.netTotal || 0,
          vatTotal: item.vatTotal || 0,
          vatAmount: item.vatAmount || item.vatTotal || 0,
          grossTotal: item.grossTotal || 0,
          warehouseId: item.warehouseId || '',
        })) || [],
        notes: offerData.notes ? `${tt('Teklif No', 'Offer No')}: ${offerData.offerNumber} - ${offerData.notes}` : `${tt('Teklif No', 'Offer No')}: ${offerData.offerNumber}`,
        currency: offerData.currency || 'TRY',
        subtotal: offerData.subtotal || 0,
        vatTotal: offerData.vatTotal || 0,
        sctTotal: offerData.sctTotal || 0,
        discountTotal: offerData.discountTotal || 0,
        grandTotal: offerData.grandTotal || 0,
      };
      
      // Items'lara vatAmount ve unitPriceWithVat ekle
      offerDataForOrder.items = offerDataForOrder.items.map((item: any) => ({
        ...item,
        vatAmount: item.vatTotal || 0,
        unitPriceWithVat: item.priceType === 'VAT_INCLUDED' 
          ? item.enteredPrice 
          : Math.round((item.unitPrice * (1 + (item.vatRate || 0) / 100)) * 100) / 100
      }));
      
      console.log('💾 localStorage\'a kaydedilen veri (sipariş):', offerDataForOrder);
      localStorage.setItem('offerToOrder', JSON.stringify(offerDataForOrder));
      
      if (currentOfferType === 'given') {
        navigate('/siparis/satis/yeni?fromOffer=true');
      } else {
        navigate('/siparis/alis/yeni?fromOffer=true');
      }
    } catch (err: any) {
      console.error('❌ Teklif detayları yüklenirken hata:', err);
      setError(err.message || 'Teklif detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Faturayı modal'da görüntüleme handler'ı
  const handleViewLinkedInvoice = async (invoiceId: string, offerType: 'given' | 'received') => {
    if (!invoiceId || !companyId) return;
    
    setLoadingInvoice(true);
    try {
      console.log('📤 [API] Fatura detayları çekiliyor:', invoiceId);
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoiceId));
      
      const result = response.data;
      console.log('📥 [API] Fatura detayı yanıtı:', result);
      
      if (!result.success || !result.data) {
        throw new Error(result.message || 'Fatura detayları yüklenemedi');
      }
      
      const invoiceData = result.data;
      
      // InvoiceDisplayModal için veriyi hazırla
      const formattedInvoice: InvoiceData = {
        id: invoiceData.id,
        invoiceNumber: invoiceData.invoiceNumber,
        account: {
          companyName: invoiceData.accountName || '',
          taxNumber: invoiceData.accountTaxNumber || '',
          taxOffice: invoiceData.accountTaxOffice || '',
          address: invoiceData.accountAddress || '',
          phone: invoiceData.accountPhone || '',
          email: invoiceData.accountEmail || '',
        },
        date: invoiceData.invoiceDate || invoiceData.date,
        dueDate: invoiceData.dueDate,
        items: invoiceData.items?.map((item: any) => ({
          itemName: item.itemName || item.name,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          vatRate: item.vatRate || 0,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          lineTotal: item.netTotal || (item.quantity * item.unitPrice),
          vatAmount: item.vatAmount || item.vatTotal || 0,
          sctAmount: item.sctAmount || 0,
          grossTotal: item.grossTotal || 0,
        })) || [],
        notes: invoiceData.notes,
        subtotal: invoiceData.subtotal || 0,
        vatTotal: invoiceData.vatTotal || 0,
        sctTotal: invoiceData.sctTotal || 0,
        discountTotal: invoiceData.discountTotal || 0,
        grandTotal: invoiceData.grandTotal || 0,
        currency: invoiceData.currency || 'TRY',
        type: offerType === 'given' ? 'sales' : 'purchase',
        sourceOfferId: invoiceData.sourceOfferId,
        sourceOfferNumber: invoiceData.sourceOfferNumber,
      };
      
      setSelectedInvoiceForDisplay(formattedInvoice);
      setIsInvoiceDisplayModalOpen(true);
    } catch (err: any) {
      console.error('❌ Fatura detayları yüklenirken hata:', err);
      setError(err.message || 'Fatura detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoadingInvoice(false);
    }
  };
  
  // ✅ Siparişi modal'da görüntüleme handler'ı
  const handleViewLinkedOrder = async (orderId: string, offerType: 'given' | 'received') => {
    if (!orderId || !companyId) return;
    
    setLoadingOrder(true);
    try {
      console.log('📤 [API] Sipariş detayları çekiliyor:', orderId);
      const response = await accountingApi.get(ACCOUNTING_API.ORDER_BY_ID(orderId));
      
      const result = response.data;
      console.log('📥 [API] Sipariş detayı yanıtı:', result);
      
      if (!result.success || !result.data) {
        throw new Error(result.message || 'Sipariş detayları yüklenemedi');
      }
      
      const orderData = result.data;
      
      // OrderDisplayModal için veriyi hazırla
      const formattedOrder: OrderData = {
        id: orderData.id,
        orderNumber: orderData.orderNumber,
        orderType: orderData.orderType || (offerType === 'given' ? 'SALES_ORDER' : 'PURCHASE_ORDER'),
        account: {
          companyName: orderData.accountName || '',
          taxNumber: orderData.accountTaxNumber || '',
          taxOffice: orderData.accountTaxOffice || '',
          address: orderData.accountAddress || '',
          phone: orderData.accountPhone || '',
          email: orderData.accountEmail || '',
        },
        orderDate: orderData.orderDate || orderData.date,
        deliveryDate: orderData.deliveryDate,
        status: orderData.status || '',
        processStock: orderData.processStock || false,
        subtotal: orderData.subtotal || 0,
        vatTotal: orderData.vatTotal || 0,
        sctTotal: orderData.sctTotal || 0,
        discountTotal: orderData.discountTotal || 0,
        grandTotal: orderData.grandTotal || 0,
        currency: orderData.currency || 'TRY',
        notes: orderData.notes,
        description: orderData.description,
        paymentMethod: orderData.paymentMethod,
        iban: orderData.iban,
        paymentNote: orderData.paymentNote,
        sourceOfferNumber: orderData.sourceOfferNumber,
        linkedInvoiceNumber: orderData.linkedInvoiceNumber || orderData.convertedInvoiceNumber,
        linkedWaybillNumber: orderData.linkedWaybillNumber || orderData.convertedWaybillNumber,
        createdAt: orderData.createdAt,
        updatedAt: orderData.updatedAt,
        items: orderData.items?.map((item: any) => ({
          itemName: item.itemName || item.name,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          vatRate: item.vatRate || 0,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          netTotal: item.netTotal || (item.quantity * item.unitPrice),
          grossTotal: item.grossTotal || 0,
          lineNumber: item.lineNumber || 0,
        })) || [],
      };
      
      setSelectedOrderForDisplay(formattedOrder);
      setIsOrderDisplayModalOpen(true);
    } catch (err: any) {
      console.error('❌ Sipariş detayları yüklenirken hata:', err);
      setError(err.message || 'Sipariş detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoadingOrder(false);
    }
  };

  // Durum çipinin rengini belirleyen fonksiyon
  const getStatusColor = (status: string) => {
    const lowerStatus = status?.toLowerCase() || '';
    switch (lowerStatus) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'default';
      case 'invoiced':
        return 'info';
      case 'ordered':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Durum Türkçe çevirisi
  const getStatusText = (status: string): string => {
    const lowerStatus = status?.toLowerCase() || '';
    switch (lowerStatus) {
      case 'approved':
        return tt('Onaylandı', 'Approved');
      case 'pending':
        return tt('Beklemede', 'Pending');
      case 'rejected':
        return tt('Reddedildi', 'Rejected');
      case 'draft':
        return tt('Taslak', 'Draft');
      case 'invoiced':
        return tt('Faturalandırıldı', 'Invoiced');
      case 'ordered':
        return tt('Siparişe Dönüştürüldü', 'Converted to Order');
      default:
        return status;
    }
  };

  // Tarihi DD.MM.YYYY formatına dönüştürme
  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Geçersiz tarih kontrolü
    
    return date.toLocaleDateString(localeCode, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Para formatı
  const formatCurrency = (amount: number, currency: string = 'TRY'): string => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };
  
  const navigate = useNavigate();

  // Şirket ID'sini yükleme
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setError('Kullanıcı oturumu bulunamadı.');
          setLoading(false);
          return;
        }
        
        const userData = await firebaseDataService.getUserData(uid);
        
        if (userData && userData.companyId) {
          setCompanyId(userData.companyId);
        } else {
          setError('Kullanıcı bilgileri bulunamadı.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Veri yükleme hatası:', err);
        setError('Veriler yüklenirken bir hata oluştu.');
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []);
  
  // Server-side sayfalama ile teklifleri yükleme
  const loadOffers = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      const offerTypeParam = offerTypeFilter === 'all' ? undefined : (offerTypeFilter === 'given' ? 'SALES_OFFER' : 'PURCHASE_OFFER');
      const statusParam = statusFilter !== 'all' ? statusFilter : undefined;
      
      const response = await accountingApi.get(ACCOUNTING_API.OFFER_BY_COMPANY_PAGED({
        page: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        offerType: offerTypeParam,
        status: statusParam,
      }));
      
      const result = response.data;
      
      if (result.success && result.data) {
        const offersList: UnifiedOffer[] = result.data.map((offer: any) => {
          const isGiven = offer.offerType === 'SALES_OFFER' || offer.offerType === 0;
          const linkedInvoiceId = offer.linkedInvoiceId || offer.convertedInvoiceId || undefined;
          const linkedInvoiceNumber = offer.linkedInvoiceNumber || undefined;
          const linkedOrderId = offer.linkedOrderId || undefined;
          const linkedOrderNumber = offer.linkedOrderNumber || undefined;
          
          const mapItems = (items: any[]) => items?.map((item: any) => ({
            id: item.id,
            name: item.itemName,
            itemName: item.itemName,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            vatRate: item.vatRate,
            sctRate: item.sctRate || 0,
            totalPrice: item.grossTotal || 0,
            grossTotal: item.grossTotal || 0
          })) || [];
          
          const originalOffer: GivenOffer | ReceivedOffer = isGiven ? {
            id: offer.id,
            offerNumber: offer.offerNumber || '',
            accountId: offer.accountId?.toString() || '',
            account: {
              companyName: offer.accountName || tt('İsimsiz Müşteri', 'Unnamed Customer'),
              taxNumber: offer.accountTaxNumber || '',
              taxOffice: offer.accountTaxOffice || '',
              address: offer.accountAddress || '',
              phone: offer.accountPhone || '',
              email: offer.accountEmail || '',
            },
            date: offer.offerDate ? offer.offerDate.split('T')[0] : '',
            validUntil: offer.validUntil ? offer.validUntil.split('T')[0] : '',
            subtotal: offer.subtotal || 0,
            vatTotal: offer.vatTotal || 0,
            sctTotal: offer.sctTotal || 0,
            discountTotal: offer.discountTotal || 0,
            grandTotal: offer.grandTotal || 0,
            status: offer.status || 'pending',
            currency: offer.currency || 'TRY',
            createdAt: offer.createdAt || '',
            updatedAt: offer.updatedAt || '',
            notes: offer.notes || '',
            linkedInvoiceId,
            linkedInvoiceNumber,
            items: mapItems(offer.items)
          } as GivenOffer : {
            id: offer.id,
            offerNumber: offer.offerNumber || '',
            supplierId: offer.accountId?.toString() || '',
            supplier: {
              companyName: offer.accountName || tt('İsimsiz Tedarikçi', 'Unnamed Supplier'),
              taxNumber: offer.accountTaxNumber || '',
              taxOffice: offer.accountTaxOffice || '',
              address: offer.accountAddress || '',
              phone: offer.accountPhone || '',
              email: offer.accountEmail || '',
            },
            date: offer.offerDate ? offer.offerDate.split('T')[0] : '',
            validUntil: offer.validUntil ? offer.validUntil.split('T')[0] : '',
            amount: offer.grandTotal || 0,
            status: offer.status || 'pending',
            currency: offer.currency || 'TRY',
            createdAt: offer.createdAt || '',
            updatedAt: offer.updatedAt || '',
            linkedInvoiceId,
            linkedInvoiceNumber,
            items: mapItems(offer.items),
            notes: offer.notes || ''
          } as ReceivedOffer;
          
          return {
            id: offer.id,
            offerNumber: offer.offerNumber || '',
            type: isGiven ? 'given' as const : 'received' as const,
            companyName: offer.accountName || (isGiven ? tt('İsimsiz Müşteri', 'Unnamed Customer') : tt('İsimsiz Tedarikçi', 'Unnamed Supplier')),
            projectName: offer.projectName || null,
            date: offer.offerDate ? offer.offerDate.split('T')[0] : '',
            validUntil: offer.validUntil ? offer.validUntil.split('T')[0] : '',
            amount: offer.grandTotal || 0,
            status: offer.status || 'pending',
            currency: offer.currency || 'TRY',
            createdAt: offer.createdAt || '',
            notes: offer.notes || '',
            linkedInvoiceId,
            linkedInvoiceNumber,
            linkedOrderId,
            linkedOrderNumber,
            originalOffer
          };
        });
        setOffers(offersList);
        setTotalCount(result.totalCount || 0);
      } else {
        setOffers([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('❌ Teklifler yüklenirken hata:', err);
      setError(err.message || 'Teklifler yüklenirken bir hata oluştu.');
      setOffers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, rowsPerPage, searchTerm, sortColumn, sortDirection, offerTypeFilter, statusFilter]);

  // Server-side yükleme - debounce ile
  useEffect(() => {
    if (!companyId) return;
    const timer = setTimeout(() => {
      loadOffers();
    }, 400);
    return () => clearTimeout(timer);
  }, [loadOffers]);

  // Server-side veriler doğrudan kullan (filtreleme/sıralama sunucuda yapılıyor)
  const filteredOffers = offers;

  // İstatistik kartları - filtrelenmiş verilere göre hesaplanır
  const offerStats = useMemo(() => {
    const pendingCount = filteredOffers.filter(o => o.status === 'pending' || o.status === 'draft').length;
    const pendingTotal = filteredOffers.filter(o => o.status === 'pending' || o.status === 'draft').reduce((sum, o) => sum + o.amount, 0);
    
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const approvedThisMonth = filteredOffers.filter(o =>
      (o.status === 'approved' || o.status === 'invoiced') &&
      new Date(o.createdAt) >= thisMonthStart
    );
    const approvedCount = approvedThisMonth.length;
    const approvedTotal = approvedThisMonth.reduce((sum, o) => sum + o.amount, 0);
    
    const rejectedCount = filteredOffers.filter(o => o.status === 'rejected').length;
    
    const totalExcludeDraft = filteredOffers.filter(o => o.status !== 'draft').length;
    const invoicedCount = filteredOffers.filter(o => o.status === 'invoiced').length;
    const conversionRate = totalExcludeDraft > 0 ? Math.round((invoicedCount / totalExcludeDraft) * 100) : 0;
    
    return { pendingCount, pendingTotal, approvedCount, approvedTotal, rejectedCount, conversionRate };
  }, [filteredOffers]);

  // Arama/filtre yapıldığında sayfayı sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm, offerTypeFilter, statusFilter, sortDirection, sortColumn]);

  // AI ajanı: sayfa filtrelerini doldur
  useEffect(() => {
    const applyAgentFilters = (req: OfferFilterRequest) => {
      if (!req) return;
      if (req.clearAll) {
        setSearchTerm('');
        setOfferTypeFilter('all');
        setStatusFilter('all');
      }
      if (req.searchText !== undefined) setSearchTerm(req.searchText);
      if (req.offerType !== undefined) {
        const t = String(req.offerType).toLowerCase();
        if (t === 'all' || t === '') setOfferTypeFilter('all');
        else if (t === 'sales' || t === 'given' || t === 'sales_offer') setOfferTypeFilter('given');
        else if (t === 'purchase' || t === 'received' || t === 'purchase_offer') setOfferTypeFilter('received');
      }
      if (req.status !== undefined) {
        const s = String(req.status).toLowerCase();
        setStatusFilter(s === '' ? 'all' : s);
      }
      setPage(0);
      setShowFilters(true);
    };
    const pending = consumePendingOfferFilters();
    if (pending) applyAgentFilters(pending);
    const handler = (e: Event) => {
      consumePendingOfferFilters();
      applyAgentFilters((e as CustomEvent<OfferFilterRequest>).detail);
    };
    window.addEventListener(OFFER_FILTERS_EVENT, handler);
    return () => window.removeEventListener(OFFER_FILTERS_EVENT, handler);
  }, []);

  // Server tarafından sayfalanmış veriler (mobil görünüm için)
  const paginatedOffers = offers;

  // AccountingDataTable kolon tanımları
  const offerColumns = useMemo<DataTableColumn<UnifiedOffer>[]>(() => [
    {
      id: 'offerNumber',
      label: tt('Teklif No', 'Offer No'),
      field: 'offerNumber',
      width: '10%',
      sortable: true,
    },
    {
      id: 'type',
      label: tt('Tür', 'Type'),
      field: 'type',
      width: '10%',
      sortable: true,
      render: (row) => (
        <Chip
          label={row.type === 'given' ? tt('Verilen', 'Given') : tt('Alınan', 'Received')}
          size="small"
          color={row.type === 'given' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      id: 'companyName',
      label: tt('Firma', 'Company'),
      field: 'companyName',
      width: '20%',
      sortable: true,
      tooltip: (row) => row.companyName,
      // Proje bağı cari adının altında küçük etiket olarak gösterilir (PYS)
      render: (row) => (
        <Box>
          <Typography variant="body2" noWrap>{row.companyName}</Typography>
          <ProjectBadge projectName={row.projectName} />
        </Box>
      ),
    },
    {
      id: 'date',
      label: tt('Tarih', 'Date'),
      field: 'date',
      width: '10%',
      sortable: true,
      render: (row) => formatDate(row.date),
    },
    {
      id: 'amount',
      label: tt('Tutar', 'Amount'),
      field: 'amount',
      width: '14%',
      sortable: true,
      // Banka/kasa görünümleriyle tam uyumlu olması için Typography kullanıldı.
      // Sadece sunum değiştirildi; yapı, click handler vs bozulmadı.
      render: (row) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          color="success.main"
          sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
        >
          {row.amount.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
          {row.currency === 'USD' ? '$' : row.currency === 'EUR' ? '€' : '₺'}
        </Typography>
      ),
    },
    {
      id: 'validUntil',
      label: tt('Geçerlilik', 'Validity'),
      field: 'validUntil',
      width: '10%',
      sortable: true,
      render: (row) => formatDate(row.validUntil),
    },
    {
      id: 'status',
      label: tt('Durum', 'Status'),
      field: 'status',
      width: '12%',
      sortable: true,
      render: (row) => (
        row.status?.toLowerCase() === 'invoiced' && row.linkedInvoiceId ? (
          <Chip
            label={loadingInvoice ? tt('Yükleniyor...', 'Loading...') : getStatusText(row.status)}
            size="small"
            color={getStatusColor(row.status) as any}
            onClick={(e) => {
              e.stopPropagation();
              handleViewLinkedInvoice(row.linkedInvoiceId!, row.type);
            }}
            // Tüm durum chip'leri eşit genişlikte görünmesi için minWidth eklendi
            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8, transform: 'scale(1.05)' }, minWidth: '160px', justifyContent: 'center' }}
            title={`${tt('Invoice', 'Invoice')}: ${row.linkedInvoiceNumber || tt('Görüntüle', 'View')}`}
          />
        ) : row.status?.toLowerCase() === 'ordered' && (row as any).linkedOrderId ? (
          <Chip
            label={loadingOrder ? tt('Yükleniyor...', 'Loading...') : getStatusText(row.status)}
            size="small"
            color={getStatusColor(row.status) as any}
            onClick={(e) => {
              e.stopPropagation();
              handleViewLinkedOrder((row as any).linkedOrderId!, row.type);
            }}
            // Tüm durum chip'leri eşit genişlikte görünmesi için minWidth eklendi
            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8, transform: 'scale(1.05)' }, minWidth: '160px', justifyContent: 'center' }}
            title={`${tt('Sipariş', 'Order')}: ${(row as any).linkedOrderNumber || tt('Görüntüle', 'View')}`}
          />
        ) : (
          <Chip
            label={getStatusText(row.status)}
            size="small"
            color={getStatusColor(row.status) as any}
            // Tüm durum chip'leri eşit genişlikte görünmesi için minWidth eklendi
            sx={{ minWidth: '160px', justifyContent: 'center' }}
          />
        )
      ),
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      width: '8%',
      align: 'center' as const,
      render: (row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e, row.id, row.type);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [loadingInvoice, loadingOrder, isTr, localeCode]);
  
  // Sil'e tıklanınca önce onay modalını aç
  const handleDeleteClick = () => {
    if (!selectedOfferId) {
      handleMenuClose();
      return;
    }
    const offer = offers.find(o => o.id === selectedOfferId);
    setDeleteConfirmOffer({
      id: selectedOfferId,
      offerNumber: offer?.offerNumber || '',
      type: selectedOfferType
    });
    handleMenuClose();
  };

  // Teklifi silme (SQL'den) — onay modalından çağrılır
  const handleDeleteOffer = async () => {
    if (!deleteConfirmOffer || !companyId) {
      setDeleteConfirmOffer(null);
      return;
    }
    
    try {
      setIsDeleting(true);
      
      const deleteDto = {
        id: deleteConfirmOffer.id,
        companyId: companyId,
        deletedBy: ownAuthSession.getCurrentEmail() || 'System'
      };
      
      console.log('📤 [SQL] Teklif siliniyor:', deleteDto);
      
      const response = await accountingApi.post(ACCOUNTING_API.OFFER_DELETE, deleteDto);
      
      const result = response.data;
      console.log('📥 [SQL] Silme yanıtı:', result);
      
      if (result.success) {
        console.log(`✅ ${deleteConfirmOffer.type === 'given' ? 'Verilen' : 'Alınan'} teklif başarıyla silindi`);
        
        // Sunucudan yeniden yükle
        loadOffers();
      } else {
        throw new Error(result.message || 'Teklif silinemedi');
      }
      
      setDeleteConfirmOffer(null);
    } catch (err: any) {
      console.error('❌ [SQL] Teklif silme hatası:', err);
      setError(err.message || 'Teklif silinirken bir hata oluştu.');
      setDeleteConfirmOffer(null);
    } finally {
      setIsDeleting(false);
    }
  };

  // Düzenleme sayfasına yönlendirme
  const handleEditOffer = (offerId: string) => {
    navigate(`/teklifler/satis/duzenle/${offerId}`);
  };

  // Görüntüleme sayfasına yönlendirme - API'den detaylı teklif bilgisi çeker
  const handleViewOffer = async (offerId: string) => {
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı.');
      return;
    }
    
    try {
      setLoading(true);
      console.log("📤 [SQL] Verilen teklif detayı yükleniyor:", offerId);
      
      const response = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(offerId));
      
      const result = response.data;
      console.log("📥 [SQL] Verilen teklif detayı yanıtı:", result);
      
      if (result.success && result.data) {
        const offerData = result.data;
        // API yanıtını GivenOfferData formatına dönüştür
        const formattedOffer: GivenOfferData = {
          id: offerData.id,
          offerNumber: offerData.offerNumber || '',
          accountId: offerData.accountId?.toString() || '',
          account: {
            companyName: offerData.accountName || tt('İsimsiz Müşteri', 'Unnamed Customer'),
            taxNumber: offerData.accountTaxNumber || '',
            taxOffice: offerData.accountTaxOffice || '',
            address: offerData.accountAddress || '',
            phone: offerData.accountPhone || '',
            email: offerData.accountEmail || '',
          },
          date: offerData.offerDate || '',
          validUntil: offerData.validUntil || '',
          subtotal: offerData.subtotal || 0,
          vatTotal: offerData.vatTotal || 0,
          sctTotal: offerData.sctTotal || 0,
          discountTotal: offerData.discountTotal || 0,
          additionalDiscountAmount: offerData.additionalDiscountAmount || 0,
          discountType: offerData.discountType || null,
          discountValue: offerData.discountValue || null,
          grandTotal: offerData.grandTotal || 0,
          status: offerData.status || 'pending',
          currency: offerData.currency || 'TRY',
          createdAt: offerData.createdAt || '',
          updatedAt: offerData.updatedAt || '',
          notes: offerData.notes || '',
          items: offerData.items?.map((item: any) => ({
            id: item.id,
            name: item.itemName,
            itemName: item.itemName,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            vatRate: item.vatRate,
            sctRate: item.sctRate || 0,
            totalPrice: item.netTotal || 0,
            grossTotal: item.grossTotal || 0
          })) || []
        };
        
        console.log("✅ Görüntülenecek verilen teklif (items dahil):", formattedOffer);
        setSelectedOffer(formattedOffer);
        setSelectedOfferModalType('given');
        setIsDisplayModalOpen(true);
      } else {
        throw new Error(result.message || 'Teklif detayı yüklenemedi');
      }
    } catch (err: any) {
      console.error('❌ Teklif detayı yüklenirken hata:', err);
      setError(err.message || 'Teklif detayı yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Alınan teklif düzenleme sayfasına yönlendirme
  const handleEditReceivedOffer = (offerId: string) => {
    navigate(`/teklifler/alis/duzenle/${offerId}`);
  };

  // Alınan teklif görüntüleme sayfasına yönlendirme - API'den detaylı teklif bilgisi çeker
  const handleViewReceivedOffer = async (offerId: string) => {
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı.');
      return;
    }
    
    try {
      setLoading(true);
      console.log("📤 [SQL] Alınan teklif detayı yükleniyor:", offerId);
      
      const response = await accountingApi.get(ACCOUNTING_API.OFFER_BY_ID(offerId));
      
      const result = response.data;
      console.log("📥 [SQL] Alınan teklif detayı yanıtı:", result);
      
      if (result.success && result.data) {
        const offerData = result.data;
        // API yanıtını ReceivedOfferData formatına dönüştür
        const formattedOffer: ReceivedOfferData = {
          id: offerData.id,
          offerNumber: offerData.offerNumber || '',
          supplierId: offerData.accountId?.toString() || '',
          supplier: {
            companyName: offerData.accountName || tt('İsimsiz Tedarikçi', 'Unnamed Supplier'),
            taxNumber: offerData.accountTaxNumber || '',
            taxOffice: offerData.accountTaxOffice || '',
            address: offerData.accountAddress || '',
            phone: offerData.accountPhone || '',
            email: offerData.accountEmail || '',
          },
          date: offerData.offerDate || '',
          validUntil: offerData.validUntil || '',
          amount: offerData.grandTotal || 0,
          status: offerData.status || 'pending',
          currency: offerData.currency || 'TRY',
          createdAt: offerData.createdAt || '',
          updatedAt: offerData.updatedAt || '',
          notes: offerData.notes || '',
          items: offerData.items?.map((item: any) => ({
            id: item.id,
            name: item.itemName,
            itemName: item.itemName,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            vatRate: item.vatRate,
            sctRate: item.sctRate || 0,
            totalPrice: item.netTotal || 0,
            grossTotal: item.grossTotal || 0
          })) || []
        };
        
        console.log("✅ Görüntülenecek alınan teklif (items dahil):", formattedOffer);
        setSelectedOffer(formattedOffer);
        setSelectedOfferModalType('received');
        setIsDisplayModalOpen(true);
      } else {
        throw new Error(result.message || 'Teklif detayı yüklenemedi');
      }
    } catch (err: any) {
      console.error('❌ Teklif detayı yüklenirken hata:', err);
      setError(err.message || 'Teklif detayı yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Modal kapatma fonksiyonu
  const handleCloseDisplayModal = () => {
    setIsDisplayModalOpen(false);
    setSelectedOffer(null);
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      
      // Verilen Teklifler sayfası
      const givenOffersSheet = workbook.addWorksheet(tt('Verilen Teklifler', 'Given Offers'));
      
      // Başlık satırı
      givenOffersSheet.mergeCells('A1:H1');
      const titleCell = givenOffersSheet.getCell('A1');
      titleCell.value = tt('Verilen Teklifler Listesi', 'Given Offers List');
      titleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      // Boş satır ekle
      givenOffersSheet.addRow([]);
      
      // Tablo başlıkları
      const headerRow = givenOffersSheet.addRow([
        tt('Teklif No', 'Offer No'),
        tt('Müşteri', 'Customer'),
        tt('Tarih', 'Date'),
        tt('Geçerlilik Tarihi', 'Valid Until'),
        tt('Para Birimi', 'Currency'),
        tt('Tutar', 'Amount'),
        tt('Durum', 'Status'),
        tt('Açıklama', 'Description')
      ]);
      
      // Başlık stili
      headerRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });
      
      // Verilen teklif verilerini ekle
      offers.filter(o => o.type === 'given').forEach((offer) => {
        givenOffersSheet.addRow([
          offer.offerNumber,
          offer.companyName,
          formatDate(offer.date),
          formatDate(offer.validUntil),
          offer.currency,
          offer.amount,
          getStatusText(offer.status),
          offer.notes || '-'
        ]);
      });
      
      // Sütun genişliklerini ayarla
      givenOffersSheet.getColumn(1).width = 15; // Teklif No
      givenOffersSheet.getColumn(2).width = 30; // Müşteri
      givenOffersSheet.getColumn(3).width = 15; // Tarih
      givenOffersSheet.getColumn(4).width = 15; // Geçerlilik Tarihi
      givenOffersSheet.getColumn(5).width = 10; // Para Birimi
      givenOffersSheet.getColumn(6).width = 15; // Tutar
      givenOffersSheet.getColumn(7).width = 15; // Durum
      givenOffersSheet.getColumn(8).width = 30; // Açıklama
      
      // Para birimi sütunu için stil
      givenOffersSheet.getColumn(6).numFmt = '#,##0.00';
      
      // Zebrastripe (alternatif satır renklendirme)
      for (let i = 4; i <= givenOffersSheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        givenOffersSheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Alınan Teklifler sayfası
      const receivedOffersSheet = workbook.addWorksheet(tt('Alınan Teklifler', 'Received Offers'));
      
      // Başlık satırı
      receivedOffersSheet.mergeCells('A1:H1');
      const receivedTitleCell = receivedOffersSheet.getCell('A1');
      receivedTitleCell.value = tt('Alınan Teklifler Listesi', 'Received Offers List');
      receivedTitleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      // Boş satır ekle
      receivedOffersSheet.addRow([]);
      
      // Tablo başlıkları
      const receivedHeaderRow = receivedOffersSheet.addRow([
        tt('Teklif No', 'Offer No'),
        tt('Tedarikçi', 'Supplier'),
        tt('Tarih', 'Date'),
        tt('Geçerlilik Tarihi', 'Valid Until'),
        tt('Para Birimi', 'Currency'),
        tt('Tutar', 'Amount'),
        tt('Durum', 'Status'),
        tt('Açıklama', 'Description')
      ]);
      
      // Başlık stili
      receivedHeaderRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });
      
      // Alınan teklif verilerini ekle
      offers.filter(o => o.type === 'received').forEach((offer) => {
        receivedOffersSheet.addRow([
          offer.offerNumber,
          offer.companyName,
          formatDate(offer.date),
          formatDate(offer.validUntil),
          offer.currency,
          offer.amount,
          getStatusText(offer.status),
          offer.notes || '-'
        ]);
      });
      
      // Sütun genişliklerini ayarla
      receivedOffersSheet.getColumn(1).width = 15; // Teklif No
      receivedOffersSheet.getColumn(2).width = 30; // Tedarikçi
      receivedOffersSheet.getColumn(3).width = 15; // Tarih
      receivedOffersSheet.getColumn(4).width = 15; // Geçerlilik Tarihi
      receivedOffersSheet.getColumn(5).width = 10; // Para Birimi
      receivedOffersSheet.getColumn(6).width = 15; // Tutar
      receivedOffersSheet.getColumn(7).width = 15; // Durum
      receivedOffersSheet.getColumn(8).width = 30; // Açıklama
      
      // Para birimi sütunu için stil
      receivedOffersSheet.getColumn(6).numFmt = '#,##0.00';
      
      // Zebrastripe (alternatif satır renklendirme)
      for (let i = 4; i <= receivedOffersSheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        receivedOffersSheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Teklif Kalemleri Detay sayfası
      const itemsSheet = workbook.addWorksheet(tt('Teklif Detayları', 'Offer Details'));
      
      // Başlık satırı
      itemsSheet.mergeCells('A1:J1');
      const itemsTitleCell = itemsSheet.getCell('A1');
      itemsTitleCell.value = tt('Teklif Kalemleri Detayları', 'Offer Items Details');
      itemsTitleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      // Boş satır ekle
      itemsSheet.addRow([]);
      
      // Tablo başlıkları
      const itemsHeaderRow = itemsSheet.addRow([
        tt('Teklif No', 'Offer No'),
        tt('Teklif Tipi', 'Offer Type'),
        tt('Müşteri/Tedarikçi', 'Customer/Supplier'),
        tt('Tarih', 'Date'),
        tt('Ürün Adı', 'Product Name'),
        tt('Birim', 'Unit'),
        tt('Miktar', 'Quantity'),
        tt('Birim Fiyat', 'Unit Price'),
        tt('KDV Oranı', 'VAT Rate'),
        tt('Toplam Tutar', 'Total Amount')
      ]);
      
      // Başlık stili
      itemsHeaderRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });
      
      // Verilen teklif kalemlerini ekle
      offers.filter(o => o.type === 'given').forEach((offer) => {
        const orig = offer.originalOffer as GivenOffer;
        if (orig.items && orig.items.length > 0) {
          orig.items.forEach((item: any) => {
            itemsSheet.addRow([
              offer.offerNumber,
              tt('Verilen Teklif', 'Given Offer'),
              offer.companyName,
              formatDate(offer.date),
              item.name || item.itemName || '-',
              item.unit || tt('Adet', 'Unit'),
              item.quantity || 0,
              item.unitPrice || 0,
              item.vatRate ? `%${item.vatRate}` : '%0',
              item.totalPrice || 0
            ]);
          });
        }
      });
      
      // Alınan teklif kalemlerini ekle
      offers.filter(o => o.type === 'received').forEach((offer) => {
        const orig = offer.originalOffer as ReceivedOffer;
        if (orig.items && orig.items.length > 0) {
          orig.items.forEach((item: any) => {
            itemsSheet.addRow([
              offer.offerNumber,
              tt('Alınan Teklif', 'Received Offer'),
              offer.companyName,
              formatDate(offer.date),
              item.name || item.itemName || '-',
              item.unit || tt('Adet', 'Unit'),
              item.quantity || 0,
              item.unitPrice || 0,
              item.vatRate ? `%${item.vatRate}` : '%0',
              item.totalPrice || 0
            ]);
          });
        }
      });
      
      // Sütun genişliklerini ayarla
      itemsSheet.getColumn(1).width = 15; // Teklif No
      itemsSheet.getColumn(2).width = 15; // Teklif Tipi
      itemsSheet.getColumn(3).width = 30; // Müşteri/Tedarikçi
      itemsSheet.getColumn(4).width = 15; // Tarih
      itemsSheet.getColumn(5).width = 30; // Ürün Adı
      itemsSheet.getColumn(6).width = 10; // Birim
      itemsSheet.getColumn(7).width = 10; // Miktar
      itemsSheet.getColumn(8).width = 15; // Birim Fiyat
      itemsSheet.getColumn(9).width = 10; // KDV Oranı
      itemsSheet.getColumn(10).width = 15; // Toplam Tutar
      
      // Sayısal formatlı sütunlar
      itemsSheet.getColumn(7).numFmt = '#,##0.00';
      itemsSheet.getColumn(8).numFmt = '#,##0.00';
      itemsSheet.getColumn(10).numFmt = '#,##0.00';
      
      // Zebrastripe (alternatif satır renklendirme)
      for (let i = 4; i <= itemsSheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        itemsSheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${tt('Teklifler_Listesi', 'Offers_List')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      console.log('Excel dışa aktarma tamamlandı.');
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(tt('Excel dışa aktarma sırasında bir hata oluştu.', 'An error occurred during Excel export.'));
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <LocalOfferIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {tt('Teklifler', 'Offers')}
          </Typography>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={loading && offers.length === 0}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<LocalOfferIcon />}
            color={STATS_COLORS.blue}
            title={`${offerStats.pendingCount} ${tt('Teklif', 'Offer')}`}
            subtitle={`${tt('Açık Teklifler', 'Open Offers')} (${formatStatsCurrency(offerStats.pendingTotal)})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CheckCircleIcon />}
            color={STATS_COLORS.green}
            title={`${offerStats.approvedCount} ${tt('Teklif', 'Offer')}`}
            subtitle={`${tt('Onaylandı - Bu Ay', 'Approved - This Month')} (${formatStatsCurrency(offerStats.approvedTotal)})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CancelIcon />}
            color={STATS_COLORS.red}
            title={`${offerStats.rejectedCount} ${tt('Teklif', 'Offer')}`}
            subtitle={tt('Reddedilen Teklifler', 'Rejected Offers')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TrendingUpIcon />}
            color={offerStats.conversionRate >= 50 ? STATS_COLORS.green : STATS_COLORS.orange}
            title={`%${offerStats.conversionRate}`}
            subtitle={tt('Tekliften Satışa Dönüş Oranı', 'Offer to Sales Conversion Rate')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı */}
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Teklif Ara...', 'Search Offer...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Teklif Türü Filtresi */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{tt('Teklif Türü', 'Offer Type')}</InputLabel>
              <Select
                value={offerTypeFilter}
                label={tt('Teklif Türü', 'Offer Type')}
                onChange={(e) => setOfferTypeFilter(e.target.value as 'all' | 'given' | 'received')}
              >
                <SelectMenuItem value="all">{tt('Tümü', 'All')}</SelectMenuItem>
                <SelectMenuItem value="given">{tt('Verilen Teklifler', 'Given Offers')}</SelectMenuItem>
                <SelectMenuItem value="received">{tt('Alınan Teklifler', 'Received Offers')}</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Filtreleri Göster İkonu */}
          <Grid item xs={12} md={0.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Tooltip title={showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreleri Göster', 'Show Filters')}>
              <IconButton
                color={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          {/* Desktop Butonlar - Aynı Satır */}
          <Grid item xs={12} md={7} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/teklifler/satis/yeni')}
              >
                {tt('Verilen Teklif', 'Given Offer')}
              </Button>
              <Button 
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/teklifler/alis/yeni')}
              >
                {tt('Alınan Teklif', 'Received Offer')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenFeaturesMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color={showFilters ? "primary" : "inherit"}
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  {showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreleri Göster', 'Show Filters')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="contained" 
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/teklifler/satis/yeni')}
                >
                  {tt('Verilen', 'Given')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/teklifler/alis/yeni')}
                >
                  {tt('Alınan', 'Received')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="outlined"
                  onClick={handleOpenFeaturesMenu}
                  sx={{ minWidth: 'auto' }}
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Filtreler - showFilters true ise göster */}
      {showFilters && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Durum Filtresi */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('Durum', 'Status')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={tt('Durum', 'Status')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <SelectMenuItem value="all">{tt('Tüm Durumlar', 'All Statuses')}</SelectMenuItem>
                  <SelectMenuItem value="draft">{tt('Taslak', 'Draft')}</SelectMenuItem>
                  <SelectMenuItem value="pending">{tt('Beklemede', 'Pending')}</SelectMenuItem>
                  <SelectMenuItem value="approved">{tt('Onaylandı', 'Approved')}</SelectMenuItem>
                  <SelectMenuItem value="rejected">{tt('Reddedildi', 'Rejected')}</SelectMenuItem>
                  <SelectMenuItem value="invoiced">{tt('Faturalandırıldı', 'Invoiced')}</SelectMenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Sıralama */}
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                onClick={() => handleSortChange('createdAt')}
              >
                {sortDirection === 'asc' ? tt('Eskiden Yeniye', 'Oldest to Newest') : tt('Yeniden Eskiye', 'Newest to Oldest')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Ana İçerik */}
      <Box sx={{ mb: 3 }}>
        {/* Tablo - loading overlay ile */}
        <Box sx={{ position: 'relative' }}>
          {/* Loading Overlay */}
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              borderRadius: 2,
            }}>
              <CircularProgress />
            </Box>
          )}
          
          {/* Desktop: AccountingDataTable */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <AccountingDataTable<UnifiedOffer>
              columns={offerColumns}
              rows={filteredOffers}
              getRowId={(row) => row.id}
              loading={loading}
              serverSidePagination={true}
              totalCount={totalCount}
              page={page}
              pageSize={rowsPerPage}
              onPageChange={(newPage, newRowsPerPage) => {
                setPage(newPage);
                setRowsPerPage(newRowsPerPage);
              }}
              onSort={(field, direction) => handleSortChange(field)}
              sortField={sortColumn}
              sortDirection={sortDirection}
              emptyMessage={tt('Henüz teklif kaydı bulunamadı.', 'No offer records found yet.')}
              emptyIcon={<LocalOfferIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
              ariaLabel={tt('teklifler tablosu', 'offers table')}
              columnResizeStorageKey="offers"
            />
          </Box>

          {/* Mobile: Card View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
            {paginatedOffers.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
                borderRadius: 2
              }}>
                <LocalOfferIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {tt('Henüz teklif kaydı bulunamadı.', 'No offer records found yet.')}
                </Typography>
              </Box>
            ) : (
              paginatedOffers.map((offer) => (
                <Card 
                  key={offer.id}
                  sx={{ 
                    border: '1px solid',
                    borderColor: offer.type === 'given' ? 'primary.light' : 'secondary.light',
                    bgcolor: offer.type === 'given' ? 'rgba(25, 118, 210, 0.04)' : 'rgba(156, 39, 176, 0.04)'
                  }}
                >
                  <CardContent>
                    {/* Başlık - Teklif No ve Tür */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalOfferIcon color={offer.type === 'given' ? 'primary' : 'secondary'} sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {offer.offerNumber}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip 
                          label={offer.type === 'given' ? tt('Verilen', 'Given') : tt('Alınan', 'Received')}
                          size="small"
                          color={offer.type === 'given' ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                        {offer.status?.toLowerCase() === 'invoiced' && offer.linkedInvoiceId ? (
                          <Chip 
                            label={loadingInvoice ? tt('Yükleniyor...', 'Loading...') : getStatusText(offer.status)}
                            size="small"
                            color={getStatusColor(offer.status) as any}
                            onClick={() => handleViewLinkedInvoice(offer.linkedInvoiceId!, offer.type)}
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                            title={`${tt('Fatura', 'Invoice')}: ${offer.linkedInvoiceNumber || tt('Görüntüle', 'View')}`}
                          />
                        ) : offer.status?.toLowerCase() === 'ordered' && offer.linkedOrderId ? (
                          <Chip 
                            label={loadingOrder ? tt('Yükleniyor...', 'Loading...') : getStatusText(offer.status)}
                            size="small"
                            color={getStatusColor(offer.status) as any}
                            onClick={() => handleViewLinkedOrder(offer.linkedOrderId!, offer.type)}
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                            title={`${tt('Sipariş', 'Order')}: ${offer.linkedOrderNumber || tt('Görüntüle', 'View')}`}
                          />
                        ) : (
                          <Chip 
                            label={getStatusText(offer.status)} 
                            size="small"
                            color={getStatusColor(offer.status) as any}
                          />
                        )}

                      </Box>
                    </Box>
                    
                    {/* Firma */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {offer.type === 'given' ? tt('Müşteri:', 'Customer:') : tt('Tedarikçi:', 'Supplier:')}
                      </Typography>
                      <Typography variant="body2">
                        {offer.companyName}
                      </Typography>
                    </Box>
                    
                    {/* Tarih ve Geçerlilik */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {tt('Tarih:', 'Date:')}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(offer.date)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {tt('Geçerlilik:', 'Validity:')}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(offer.validUntil)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Tutar */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2, 
                      pt: 1.5, 
                      borderTop: '1px solid', 
                      borderColor: 'divider' 
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {tt('Tutar:', 'Amount:')}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color={offer.type === 'given' ? 'primary.main' : 'secondary.main'}>
                        {formatCurrency(offer.amount, offer.currency)}
                      </Typography>
                    </Box>
                    
                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => offer.type === 'given' ? handleViewOffer(offer.id) : handleViewReceivedOffer(offer.id)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => offer.type === 'given' ? handleEditOffer(offer.id) : handleEditReceivedOffer(offer.id)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={(e) => handleMenuOpen(e, offer.id, offer.type)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
            
            {/* Mobile Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              sx={{
                '.MuiTablePagination-toolbar': {
                  paddingLeft: 1,
                  paddingRight: 1
                },
                '.MuiTablePagination-selectLabel': {
                  display: 'none'
                },
                '.MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* 3 Nokta (Özellikler) Menüsü */}
      <Menu
        anchorEl={anchorElFeatures}
        open={Boolean(anchorElFeatures)}
        onClose={handleCloseFeaturesMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={() => {
            exportToExcel();
            handleCloseFeaturesMenu();
          }}
          disabled={loadingExport || loading || offers.length === 0}
        >
          {loadingExport ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />}
          {tt('Excel İndir', 'Download Excel')}
        </MenuItem>
        <MenuItem 
          onClick={() => {
            navigate('/teklifler/sablon-editor');
            handleCloseFeaturesMenu();
          }}
        >
          <PaletteIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Şablon Düzenle', 'Edit Template')}
        </MenuItem>
      </Menu>
      
      {/* İşlemler Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          if (selectedOfferType === 'given') {
            handleViewOffer(selectedOfferId!);
          } else {
            handleViewReceivedOffer(selectedOfferId!);
          }
          handleMenuClose();
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Görüntüle', 'View')}
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedOfferType === 'given') {
            handleEditOffer(selectedOfferId!);
          } else {
            handleEditReceivedOffer(selectedOfferId!);
          }
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Düzenle', 'Edit')}
        </MenuItem>
        <MenuItem onClick={handleDownloadPdf}>
          <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('PDF İndir', 'Download PDF')}
        </MenuItem>
        {/* Fatura/Sipariş butonları - onaylı, faturalandırılmış veya siparişe dönüştürülmüş tekliflerde */}
        {(() => {
          const selectedOffer = offers.find(o => o.id === selectedOfferId);
          const allowedStatuses = ['approved', 'invoiced', 'ordered'];
          if (!selectedOffer || !allowedStatuses.includes(selectedOffer.status?.toLowerCase())) return null;

          return (
            <>
              {/* Fatura: Göster veya Oluştur */}
              {selectedOffer.linkedInvoiceId ? (
                <MenuItem onClick={() => {
                  handleViewLinkedInvoice(selectedOffer.linkedInvoiceId!, selectedOffer.type);
                  handleMenuClose();
                }}>
                  <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                  {tt('Fatura Göster', 'View Invoice')} {selectedOffer.linkedInvoiceNumber ? `(${selectedOffer.linkedInvoiceNumber})` : ''}
                </MenuItem>
              ) : (
                <MenuItem onClick={handleConvertToInvoice}>
                  <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
                  {tt('Fatura Oluştur', 'Create Invoice')}
                </MenuItem>
              )}
              {/* Sipariş: Göster veya Oluştur */}
              {selectedOffer.linkedOrderId ? (
                <MenuItem onClick={() => {
                  handleViewLinkedOrder(selectedOffer.linkedOrderId!, selectedOffer.type);
                  handleMenuClose();
                }}>
                  <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                  {tt('Sipariş Göster', 'View Order')} {selectedOffer.linkedOrderNumber ? `(${selectedOffer.linkedOrderNumber})` : ''}
                </MenuItem>
              ) : (
                <MenuItem onClick={handleConvertToOrder}>
                  <ShoppingCartIcon fontSize="small" sx={{ mr: 1 }} />
                  {tt('Sipariş Oluştur', 'Create Order')}
                </MenuItem>
              )}
            </>
          );
        })()}
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Sil', 'Delete')}
        </MenuItem>
      </Menu>

      {/* Teklif Silme Onay Modalı */}
      <Dialog
        open={!!deleteConfirmOffer}
        onClose={isDeleting ? undefined : () => setDeleteConfirmOffer(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{tt('Teklifi Sil', 'Delete Offer')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteConfirmOffer?.offerNumber ? (
              <>
                <strong>{deleteConfirmOffer.offerNumber}</strong>{' '}
                {tt('numaralı teklifi silmek istediğinize emin misiniz?', 'Are you sure you want to delete this offer?')}
              </>
            ) : (
              tt('Bu teklifi silmek istediğinize emin misiniz?', 'Are you sure you want to delete this offer?')
            )}{' '}
            {tt('Bu işlem geri alınamaz.', 'This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOffer(null)}
            color="inherit"
            disabled={isDeleting}
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            onClick={handleDeleteOffer}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? tt('Siliniyor...', 'Deleting...') : tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Teklif Görüntüleme Modalı */}
      {selectedOffer && (
        <OfferDisplayModal
          open={isDisplayModalOpen}
          onClose={handleCloseDisplayModal}
          offer={selectedOffer}
          offerType={selectedOfferModalType}
          companyId={companyId}
        />
      )}
      
      {/* Fatura Görüntüleme Modalı */}
      <InvoiceDisplayModal
        open={isInvoiceDisplayModalOpen}
        onClose={() => {
          setIsInvoiceDisplayModalOpen(false);
          setSelectedInvoiceForDisplay(null);
        }}
        invoice={selectedInvoiceForDisplay}
        companyId={companyId}
      />
      
      {/* Sipariş Görüntüleme Modalı */}
      <OrderDisplayModal
        open={isOrderDisplayModalOpen}
        onClose={() => {
          setIsOrderDisplayModalOpen(false);
          setSelectedOrderForDisplay(null);
        }}
        order={selectedOrderForDisplay}
        companyId={companyId}
      />
    </>
  );
};

export default Offers; 